import type { JSX } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { DataTableFilters } from '@/shared/components/DataTableFilters';
import { ResultCount } from '@/shared/components/ResultCount';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { useJobPositions } from '../hooks/useJobPositions';
import { useDeleteJobPosition } from '../hooks/useDeleteJobPosition';
import { useArchiveJobPosition } from '../hooks/useArchiveJobPosition';
import { JobPositionList } from '../components/JobPositionList';
import { CreateJobPositionDialog } from '../components/CreateJobPositionDialog';
import { EditJobPositionDialog } from '../components/EditJobPositionDialog';
import { jobPositionTableColumns } from '../config/jobPositionTableColumns';
import type { JobPosition } from '../types/jobPosition.types';

export function JobPositionsPage(): JSX.Element {
  const navigate = useNavigate();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingJobPosition, setEditingJobPosition] = useState<JobPosition | null>(null);

  const { jobPositions, hasData, isLoading, isError } = useJobPositions();
  const {
    filteredSorted,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    sort,
    toggleSort,
    activeFilterCount,
  } = useTableFilters(jobPositions, jobPositionTableColumns);
  const { deleteJobPosition } = useDeleteJobPosition();
  const { archiveJobPosition } = useArchiveJobPosition();

  const handleEdit = (jobPosition: JobPosition): void => {
    setEditingJobPosition(jobPosition);
  };

  const handleCreate = (): void => {
    setIsCreateDialogOpen(true);
  };

  const handleNavigate = (jobPosition: JobPosition): void => {
    navigate(`/positions/${jobPosition.id}`);
  };

  const handleDelete = (id: string): void => {
    deleteJobPosition(id, {
      onSuccess: () => toast.success('Job position deleted successfully'),
      onError: () => toast.error('Failed to delete job position'),
    });
  };

  const handleArchive = (id: string, archive: boolean): void => {
    archiveJobPosition(
      { id, archive },
      {
        onSuccess: () =>
          toast.success(`Job position ${archive ? 'archived' : 'unarchived'} successfully`),
        onError: () => toast.error(`Failed to ${archive ? 'archive' : 'unarchive'} job position`),
      }
    );
  };

  return (
    <div className="container py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">Job Positions</h1>
          {!isLoading && !(isError && !hasData) && <ResultCount count={filteredSorted.length} />}
        </div>
        <Button onClick={handleCreate}>Create Job Position</Button>
      </div>

      <DataTableFilters
        columns={jobPositionTableColumns}
        filters={filters}
        activeFilterCount={activeFilterCount}
        onFilterChange={setFilter}
        onClearFilters={clearFilters}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />

      {isError && (
        <div className="text-center py-8 text-destructive">
          An error occurred while loading job positions
        </div>
      )}

      <JobPositionList
        jobPositions={filteredSorted}
        isLoading={isLoading}
        sort={sort}
        onToggleSort={toggleSort}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onArchive={handleArchive}
        onNavigate={handleNavigate}
      />

      <CreateJobPositionDialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} />

      {editingJobPosition && (
        <EditJobPositionDialog
          open={!!editingJobPosition}
          onOpenChange={(open) => !open && setEditingJobPosition(null)}
          jobPosition={editingJobPosition}
        />
      )}
    </div>
  );
}
